import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, TextInput } from "react-native";
import { Text, View } from "../../shared/Themed";
import FeatherIcon from "react-native-vector-icons/Feather";
import { ReadOnlyProps } from "../../../utils/templates";
import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";

interface Props {
  childern?: React.ElementType;
  titleName: string;
  isSmallDevice?: boolean;
  isMobileDevice?: boolean;
  isEditable?: boolean;
  ref?: any;
  isResetFields?: boolean;
  handleSearch: (searchText: string) => void;
  setRefresh: (event: any) => void;
  setEditable?: (event: any) => void;

  //for excel download
  dataToExport?: Array<unknown>;
  fileName?: string;
  sheetName?: string;

  // J
  padding?: boolean;
  disableTitle?: boolean;
  noBorder?: boolean;
  extraControls?: React.ReactNode;
  onExport?: () => void;
  onSearchTextChange?: (text: string) => void;
  _searchText?: string;
}

export const LabelBannerWithSearchBar = (props: ReadOnlyProps<Props>) => {
  const Children = (props.childern || {}) as React.ElementType;
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext
  );

  const [_searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    // if search text is empty, then return all data
    if (_searchText.trim().length === 0) {
      props.handleSearch("");
      return;
    }

    props.handleSearch(_searchText.trim());
  }, [_searchText]);

  useEffect(() => {
    if (props.isResetFields) {
      setSearchText("");
    }
  }, [props.isResetFields]);

  const submitSearch = () => {
    props.handleSearch(_searchText.trim());
  };

  const isExportAllowed = () => {
    return (
      !isSmallDevice &&
      !isMobileDevice &&
      props.dataToExport &&
      props.dataToExport.length > 0
    );
  };

  const isMobile = isSmallDevice || isMobileDevice;
  const showInlineTitle = !isMobile && props.disableTitle == undefined;

  return (
    <View>
      <View style={styles.container}>
        {/* Search row */}
        <View
          style={[
            styles.searchRow,
            showInlineTitle && { justifyContent: "space-between" },
            props.isEditable && { backgroundColor: "#FEF3C7" },
            isMobile && styles.searchRowMobile,
          ]}
        >
          {/* Inline title for desktop */}
          {showInlineTitle && (
            <Text style={styles.inlineTitle}>{props.titleName}</Text>
          )}

          {/* Search controls - grouped on right for desktop */}
          <View style={[
            styles.searchControls,
            !showInlineTitle && { width: "100%" },
            isMobile && styles.searchControlsMobile,
          ]}>
            {props.isEditable && (
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => props.setEditable?.(!props.isEditable)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}

            {/* Extra controls (advanced search) rendered before refresh button on desktop */}
            {!isMobile && props.extraControls}
            <TouchableOpacity
              style={[styles.refreshButton, isMobile && styles.buttonMobile]}
              onPress={() => {
                setSearchText("");
                props.setRefresh(true);
              }}
            >
              <FeatherIcon name="refresh-cw" size={isMobile ? 14 : 15} color="#6B7280" />
            </TouchableOpacity>

            <View style={[
              styles.searchContainer,
              showInlineTitle && styles.searchContainerCompact,
              isMobile && styles.searchContainerMobile,
            ]}>
              <FeatherIcon
                name="search"
                size={isMobile ? 14 : 16}
                color="#9CA3AF"
                style={{ marginRight: 8 }}
              />
              <TextInput
                ref={props.ref}
                style={[styles.searchInput, isMobile && styles.searchInputMobile]}
                onChangeText={(value) => setSearchText(value)}
                value={_searchText || ""}
                placeholder="Search..."
                placeholderTextColor="#9CA3AF"
                onSubmitEditing={() =>
                  _searchText.trim().length < 3 ? {} : submitSearch()
                }
              />
            </View>

            {/* Extra controls rendered after search bar on mobile */}
            {isMobile && props.extraControls}

            {(isExportAllowed() || props.onExport) && (
              <TouchableOpacity
                style={[styles.exportButton, isMobile && styles.buttonMobile]}
                onPress={() => {
                  if (props.onExport) {
                    props.onExport();
                  } else {
                    exportDataToExcel(
                      props.dataToExport || [],
                      props.fileName,
                      props.sheetName
                    );
                  }
                }}
              >
                <FeatherIcon name="download" size={isMobile ? 14 : 15} color="#6B7280" />
              </TouchableOpacity>
            )}


          </View>
        </View>



        {props.childern && <Children {...props} />}
      </View>
    </View >
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  titleRow: {
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  inlineTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
    flexShrink: 0,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
    paddingVertical: 12,
    paddingHorizontal: 12,
    minHeight: 56,
  },
  searchRowMobile: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    minHeight: 40,
    gap: 6,
  },
  searchControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  searchControlsMobile: {
    gap: 6,
  },
  searchContainerCompact: {
    width: 400,
    flex: undefined,
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 44,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#111827",
    paddingVertical: 0,
    outlineStyle: "none",
  } as any,
  searchInputMobile: {
    fontSize: 13,
  },
  searchContainerMobile: {
    height: 34,
    borderRadius: 8,
    paddingHorizontal: 8,
  },
  buttonMobile: {
    width: 34,
    height: 34,
    borderRadius: 8,
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  exportButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: "#6B7280",
  },
  cancelButtonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});

